import React, { useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { formatCurrency } from "../../utils/namesFormater";
import { formatProductType } from "../../utils/enumFormatter";
import StyledDataGrid from "../../shared/StyledDataGrid";
import { MimshakData } from "../../interfaces/DTO/mimshakData";
import { ManualMimshakData, ManualProductType } from "../../interfaces/DTO/manualMimshakData";
import { productTypeUniversalColumn, handlerCodeUniversalColumn, currencyColumn, policyNumberWithPathTooltipColumn, percentageColumn } from "../../shared/UIParts/columnsFormatter";
import { useContactNameColumn } from "../../hooks/useContactNameColumn";
import InvestmentPortfolioDrawer from "./InvestmentPortfolioDrawer";
import { Product } from "../../interfaces/DTO/mislaka_xml_models";

interface ContactInvestmentMimshakDataTableProps {
  data: MimshakData[];
  viewedType: "USER" | "FAMILY";
}

/**
 * טבלה להצגת תיקי השקעות (INVESTMENT_PORTFOLIO = 600)
 * מציגה: סוג מוצר, מספר פוליסה, יצרן, יתרה כוללת, תקופה מדווחת
 * ושם לקוח אם יש יותר מאחד
 */
const ContactInvestmentMimshakDataTable: React.FC<ContactInvestmentMimshakDataTableProps> = ({
  data,
  viewedType
}) => {
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [selectedMimshakData, setSelectedMimshakData] = useState<ManualMimshakData | null>(null);
  const contactNameColumn = useContactNameColumn();

  const handleRowClick = (params: any) => {
    const originalRow = data[params.id] as ManualMimshakData;
    setSelectedMimshakData(originalRow);
  };

  const handleCloseDrawer = () => {
    setSelectedMimshakData(null);
  };

  // הוספת id לכל שורה
  const rows = useMemo(() => {
    return data.map((row, index) => ({
      ...row,
      id: index,
    }));
  }, [data]);

  const columns: GridColDef[] = useMemo(() => {
    const baseCols: GridColDef[] = [
      productTypeUniversalColumn(),
      policyNumberWithPathTooltipColumn(),
      handlerCodeUniversalColumn(),
      currencyColumn("funds_sum", "יתרה כוללת", 120, true),
      percentageColumn("saving_fee", "דמי ניהול שנתיים", 130),
      { field: "reported_period", headerName: "תקופה מדווחת", width: 140 },
    ];

    // הוספת עמודת שם לקוח אם זה משפחה (יותר מלקוח אחד)
    if (viewedType === "FAMILY") {
      baseCols.unshift(contactNameColumn);
    }

    // עיצוב כותרות
    return baseCols.map((col) => ({
      ...col,
      headerClassName: "investment-table-header",
      headerAlign: "center",
    }));
  }, [viewedType, contactNameColumn]);

  const selectedData = useMemo(() => {
    if (selectedRows.length === 0) {
      return data;
    }
    return data.filter((_, index) => selectedRows.includes(index));
  }, [data, selectedRows]);

  const totals = useMemo(() => ({
    funds_sum: selectedData.reduce((sum, r) => sum + (r.funds_sum || 0), 0),
  }), [selectedData]);

  if (data.length === 0) {
    return null;
  }

  return (
    <Box dir="rtl" sx={{ maxWidth: "100%", margin: "auto", padding: 2 }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        תיקי השקעות
      </Typography>

      <StyledDataGrid
        rows={rows}
        columns={columns}
        onRowClick={handleRowClick}
        checkboxSelection
        maxHeight="600px"
        disableRowSelectionOnClick
        rowSelectionModel={selectedRows}
        onRowSelectionModelChange={(newSelection) =>
          setSelectedRows(newSelection as number[])
        }
        hideFooter={rows.length <= 100 && selectedRows.length === 0}
      />

      <Box
        sx={{
          mt: 2,
          display: "flex",
          flexWrap: "wrap",
          gap: 4,
          px: 2,
        }}
      >
        {/* תקופת דיווח */}
        {data[0]?.reported_period && (
          <Box>
            לתקופת דיווח: <strong>{data[0].reported_period}</strong>
          </Box>
        )}

        {totals.funds_sum ? (
          <Box>
            סה"כ יתרה כוללת:{" "}
            <strong>
              <span dir="ltr">{formatCurrency(totals.funds_sum)}</span>
            </strong>
          </Box>
        ) : null}
      </Box>

      {selectedMimshakData && (
        <InvestmentPortfolioDrawer
          manualMimshakData={selectedMimshakData}
          onClose={handleCloseDrawer}
          contactName={selectedMimshakData.data?.products?.[0]?.client?.client_name}
        />
      )}
    </Box>
  );
};

export default ContactInvestmentMimshakDataTable;
